import { CardHeader,
  SwipeableDrawer,
  useMediaQuery,
  Box,
  Button,
  Card,
  CardContent,
  FormHelperText,
  Grid, 
  Stack} from "@mui/material";
import { ReduxState, useGetOne } from "ra-core"
import { FC, useCallback, useState } from "react";
import { useFormik } from "formik";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "~/components/formik/OdForm";
import TextField from "~/components/formik/TextField";
import { useEffect } from "react";
import { useNetworkConfigurationContext } from "~/pages/deployment/useNetworkConfigurationContext";
import ReadonlyField from "../input/ReadonlyField";
import { NetworkConnectionType, NetworkGatewayStatus } from "~/gen/api/deploy";
import { useSelector } from "react-redux";


export interface NewNetworkConnectionProps {
  open: boolean;
  setOpen: (x:boolean) => void;
}

const validationSchema =
  Yup.object().shape({
    name: Yup.string().max(255).required('Name is required')
      .matches(/^[a-zA-Z0-9_]+$/, 'The name may contain only letters, numbers or underscores'),
    localPublicIp: Yup.string().max(255).required('Local Public Ip is required'),
    localAddressSpaces: Yup.string().max(255).required('Local Address Spaces is required')
      .matches(/^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/(3[0-2]|[1-2][0-9]|[1-9]))$/, 
        'Invalid address space, please input CIDR notation'),
  })

const initialValues = {
  submit: '',
  name: '',
  localPublicIp: '',
  localAddressSpaces: '',
}

const NewNetworkConnection: FC<NewNetworkConnectionProps> = (props) => {
  const keyResourceReady = useSelector((store:ReduxState) => !!store.admin.resources['random-key'])
  if (!keyResourceReady) return null
  
  return (
    <NewNetworkConnectionBody {...props} />
  )
}

const NewNetworkConnectionBody: FC<NewNetworkConnectionProps> = ({
  open,
  setOpen,
}) => {
  const { createConnection, vpnGatewayRec } = useNetworkConfigurationContext()

  const handleClose = (): void => {
    setOpen(false);
    resetForm({
      values: initialValues,
    })
  };

  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const [keyId, setKeyId] = useState((new Date()).getTime())
  const { data: randomKey } = useGetOne<{id:string,key:string}>('random-key', keyId)
  useEffect(() => {
    if (open) { setKeyId((new Date()).getTime()) }
  }, [open])

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
    resetForm,
  }) => {
    const options = {
      onSuccess: () => {
        resetForm({
          status: { success: true },
          values: initialValues,
          isSubmitting: false,
        })
        setOpen(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    createConnection({...values, 
      localAddressSpaces: [values.localAddressSpaces || ''],
      type: NetworkConnectionType.SiteToSite,
      gatewayId: vpnGatewayRec?.networkGatewayId,
      sharedKey: randomKey?.key || '',
    }, options)
  }, [createConnection, randomKey])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, resetForm } = formikCtrl

  if (!vpnGatewayRec || vpnGatewayRec.status !== NetworkGatewayStatus.Success) return null

  return (
    <SwipeableDrawer
      anchor="right"
      onOpen={() => {}}
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      <Card 
        sx={{ 
          overflowY: 'auto',
          mb: 4,
        }}
      >
        <CardHeader title="New Network Connection" />
        <CardContent>
          <OdForm formikCtrl={formikCtrl}>

            <Card>
              <CardContent>
                <Grid
                  container
                  spacing={4}
                >
                  <Grid item xs={12} >
                    <TextField label="Connection Name*" name="name" />
                  </Grid>
                  <Grid item xs={12} >
                    <ReadonlyField label="VPN Gateway" value={vpnGatewayRec.name || ''} />
                  </Grid>
                  <Grid item xs={12} >
                    <ReadonlyField label="OnData Gateway Endpoint" value={vpnGatewayRec.onDataPublicIp || ''} />
                  </Grid>
                  <Grid item xs={12} >
                    <TextField label="Local Gateway Public IP*" name="localPublicIp" />
                  </Grid>
                  <Grid item xs={12} >
                    <TextField label="Local Address Spaces*" name="localAddressSpaces" />
                  </Grid>
                </Grid>
                {errors.submit && (
                  <Box sx={{ mt: 3 }}>
                    <FormHelperText error>
                      {errors.submit}
                    </FormHelperText>
                  </Box>
                )}
              </CardContent>
              <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }} >
                <Button
                  color="primary"
                  disabled={isSubmitting}
                  variant="outlined"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  disabled={isSubmitting}
                  type="submit"
                  variant="contained"
                >
                  Add Network Connection
                </Button>
              </Stack>
            </Card>

          </OdForm>
        </CardContent>
      </Card>
    </SwipeableDrawer>
  )
}


export default NewNetworkConnection;


